import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { unwrap } from '@/lib/rpc';
import type { Industry, StoreCategory } from '@/types/domain';
import { defaultCurrencyFor, getSite } from '@/lib/site';

// Reference data changes rarely: cache aggressively to avoid repeat requests.
const LONG = 30 * 60 * 1000;

export function useIndustries() {
  return useQuery({
    queryKey: ['industries'],
    staleTime: LONG,
    queryFn: async () => unwrap(await supabase.from('industries').select('*').order('sort_order').order('name')) as Industry[],
  });
}

export function useStoreCategories() {
  return useQuery({
    queryKey: ['store-categories'],
    staleTime: LONG,
    queryFn: async () => unwrap(await supabase.from('store_categories').select('*').order('sort_order').order('name')) as StoreCategory[],
  });
}

export interface PublicSettings {
  supported_currencies: string[];
  default_currency: string;
  min_questions_for_submission: number;
  /** Minimum price for a provider's product, sale price included. 0 = no minimum. */
  min_product_price_cents: number;
  /** Providers need a current verified credential to submit products. */
  require_verified_credential: boolean;
  max_preview_percentage: number;
  provider_terms_version: string;
  processing_fee_bearer: 'platform' | 'provider';
  ai_tools_enabled: boolean;
  plus: { enabled: boolean; price_cents: number; currency: string; interval: 'month' | 'year' };
  ai_explain_daily_limit: number;
  /** Most new modules the Workshop may add in one run (0 = never). */
  workshop_max_new_modules: number;
  /** Show the New section on the Store home page. */
  store_show_new: boolean;
}

export function usePublicSettings() {
  return useQuery({
    queryKey: ['public-settings'],
    staleTime: LONG,
    queryFn: async () => {
      const rows = unwrap(await supabase.from('platform_settings').select('key, value').eq('is_public', true)) as { key: string; value: unknown }[];
      const map = Object.fromEntries(rows.map((r) => [r.key, r.value]));
      return {
        supported_currencies: (map.supported_currencies as string[]) ?? [getSite().currency],
        default_currency: defaultCurrencyFor(map.supported_currencies as string[] | undefined, (map.default_currency as string) ?? getSite().currency),
        min_questions_for_submission: Number(map.min_questions_for_submission ?? 10),
        min_product_price_cents: Number(map.min_product_price_cents ?? 0),
        require_verified_credential: map.require_verified_credential !== false,
        max_preview_percentage: Number(map.max_preview_percentage ?? 25),
        provider_terms_version: (map.provider_terms_version as string) ?? '',
        processing_fee_bearer: map.processing_fee_bearer === 'provider' ? 'provider' : 'platform',
        ai_tools_enabled: map.ai_tools_enabled !== false,
        plus: {
          enabled: false,
          price_cents: 0,
          currency: getSite().currency,
          interval: 'month',
          ...((map.plus_subscription as Partial<PublicSettings['plus']>) ?? {}),
        },
        ai_explain_daily_limit: Number(map.ai_explain_daily_limit ?? 30),
        workshop_max_new_modules: Math.min(20, Math.max(0, Number(map.workshop_max_new_modules ?? 5))),
        store_show_new: map.store_show_new !== false,
      } satisfies PublicSettings;
    },
  });
}
